<script>
import { mapGetters } from 'vuex';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const SDK_SCRIPT_ID = 'canny-jssdk';
const SDK_SRC = 'https://sdk.canny.io/sdk.js';
// Observed popover height is ~429-453px; require that plus a small margin
// above the trigger before opening upward.
const CHANGELOG_MIN_SPACE_ABOVE = 470;

// Renderless loader for the Canny changelog widget. Mirrors the injection
// pattern of CloudchatSupportWidget.vue: gated, idempotent, config read from
// window.chatwootConfig. The visible trigger lives in the primary sidebar rail
// (Primary.vue) as a button carrying `data-canny-changelog` — Canny attaches
// its own unread badge and popover to that element.
//
// initChangelog binds to the [data-canny-changelog] element present at call
// time. Primary.vue is destroyed/recreated on layout switches, so the trigger
// re-mounts as a new element; this component re-runs initChangelog on every
// CANNY_CHANGELOG_TRIGGER_MOUNTED so the freshly rendered button is rebound.
export default {
  name: 'CannyChangelogWidget',
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
    }),
    appID() {
      return window.chatwootConfig?.cannyAppID || '';
    },
    // Same gate as the trigger button in Primary.vue: the changelog ships to
    // every account whenever there is an appID to load (and an account has
    // resolved). Kill switch: deploy with CANNY_APP_ID set to an empty string.
    canShowWidget() {
      return Boolean(this.accountId && this.appID);
    },
  },
  watch: {
    // Gate can flip after mount (account resolves async). Boot on the first
    // `true`; both calls are idempotent.
    canShowWidget(canShow) {
      if (canShow) this.boot();
    },
  },
  mounted() {
    this.$emitter.on(
      BUS_EVENTS.CANNY_CHANGELOG_TRIGGER_MOUNTED,
      this.onTriggerMounted
    );
    // Boot directly too: on a hard reload straight into the dashboard the
    // trigger button (Primary.vue) mounts BEFORE this component registers the
    // listener above, so its mounted-announcement is lost. Calling
    // initChangelog here binds the already-rendered button; the event covers
    // buttons that (re)render later.
    if (this.canShowWidget) this.boot();
  },
  beforeDestroy() {
    this.$emitter.off(
      BUS_EVENTS.CANNY_CHANGELOG_TRIGGER_MOUNTED,
      this.onTriggerMounted
    );
  },
  methods: {
    boot() {
      this.injectSdk();
      this.initChangelog();
    },
    // Fired by Primary.vue whenever the trigger button (re)mounts. Rebinding on
    // every mount is what keeps the button working after a layout switch.
    onTriggerMounted() {
      if (!this.canShowWidget) return;
      this.boot();
    },
    // Loads the Canny SDK (https://developers.canny.io/install/changelog). A
    // clean rewrite of Canny's official snippet: define the queueing stub so
    // initChangelog can be called before sdk.js finishes loading, then append
    // the script once. Idempotent — guarded by the appID and the script id.
    injectSdk() {
      if (!this.appID) return;
      this.ensureCannyQueue();
      if (document.getElementById(SDK_SCRIPT_ID)) return;
      const script = document.createElement('script');
      script.id = SDK_SCRIPT_ID;
      script.async = true;
      script.src = SDK_SRC;
      (document.head || document.documentElement).appendChild(script);
    },
    // window.Canny buffers calls into `q` until the real SDK loads and drains
    // it. Rest params (not `arguments`) so pre-load calls forward cleanly.
    ensureCannyQueue() {
      if (typeof window.Canny === 'function') return;
      const canny = (...args) => canny.q.push(args);
      canny.q = [];
      window.Canny = canny;
    },
    // Canny honours `position` literally — it does NOT flip when the popover
    // would not fit (measured: with the trigger 128px from the top, a 'top'
    // popover renders at y=-310, i.e. 310px cut off above the viewport).
    //
    // The trigger lives in the sidebar rail, and how far down it sits depends
    // on how many nav items the account has — which is feature-flag driven, so
    // it varies per account and cannot be hardcoded. Measure the rail anchor
    // and open upward only when the popover actually fits above it.
    //
    // Measuring at open time is the only lever we have. The panel is a
    // fixed-height cross-origin iframe that Canny absolutely-positions on
    // <body>, and it computes that position once, on open — it never reflows on
    // resize. Its height and internal scrolling are not configurable either,
    // and capping #canny-changelog-iframe with max-height does not help: the
    // box gets `overflow: clip` while the iframe keeps `height: 453px` inline,
    // so it crops the panel instead of scrolling it, hiding the "See all
    // changes" footer. Opening in the wrong direction is therefore unrecoverable
    // — downward from a low trigger it also stretches the document, adding a
    // phantom page scrollbar that cannot scroll the fixed rail back into view.
    changelogPosition() {
      const anchor = document.querySelector('[data-canny-changelog-anchor]');
      if (!anchor) return 'bottom';
      const { bottom } = anchor.getBoundingClientRect();
      return bottom >= CHANGELOG_MIN_SPACE_ABOVE ? 'top' : 'bottom';
    },
    initChangelog() {
      if (typeof window.Canny !== 'function' || !this.appID) return;
      // Left-aligned because the rail hugs the left edge. Direction is chosen
      // per account — see changelogPosition.
      window.Canny('initChangelog', {
        appID: this.appID,
        position: this.changelogPosition(),
        align: 'left',
        theme: 'auto',
      });
    },
  },
  render() {
    return null;
  },
};
</script>
